import React, { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import classNames from 'classnames';

import {
  TbCalendar,
  TbClock,
  TbCurrencyEuro,
  TbPuzzle,
  TbRepeat,
} from 'react-icons/tb';

import { createLocale } from 'locales';

import Typography from 'components/typography/typography';

import useHidePeriodJob from 'state/remote/remote-organization/use-hide-period-job';

import useClosePeriodJob from 'state/remote/remote-organization/use-close-period-job';

import type { OrganisationPeriodJob } from 'state/__generated__/tadaahAPI';

import {
  formatDateToLocaleNetherlands,
  formatJobDurationDisplay,
} from 'utils/display-text-formatter';

import Button from 'components-new/button/button';

import Modal from 'components-new/modal/modal';

import Banner from 'components-new/banner/banner';

import PairText from './PairText';

interface PeriodJobCardProps {
  job: OrganisationPeriodJob;
}

const localize = createLocale('organisationPeriodJobsLocales');

const PeriodJobCard: React.FC<PeriodJobCardProps> = (props) => {
  const { job } = props;
  const { hidePeriodJob } = useHidePeriodJob();
  const { closePeriodJob } = useClosePeriodJob();
  const [searchParams, setSearchParams] = useSearchParams({});

  const handleDetailsClicked = useCallback(() => {
    const id = job.id?.toString() || '';
    if (!job.is_confirmed) setSearchParams({ job_id: id });
    else if (job.is_confirmed)
      setSearchParams({ assignment_details: 'true', job_id: id });
  }, [job.id, job.is_confirmed, setSearchParams]);

  const boxShadow = '0px 2px 4px 0px rgba(0, 0, 0, 0.06)';
  const cardClassName = classNames(
    'my-4 py-4 px-6 mx-auto bg-white border rounded-md border-solid border-[#E7E6E8]',
  );

  const handleHidingClosingActions = useCallback(() => {
    if (job.id && job.status === 'C') {
      searchParams.append(`confirm_hide_job_${job.id}`, 'true');
      setSearchParams(searchParams);
    }
    if (job.id && job.status === 'O') {
      searchParams.append(`confirm_close_job_${job.id}`, 'true');
      setSearchParams(searchParams);
    }
  }, [job.id, job.status, searchParams, setSearchParams]);

  const mutateJobStatus = useCallback(() => {
    if (job.id && job.status === 'C') {
      hidePeriodJob({ jobId: job.id?.toString() });
    }
    if (job.id && job.status === 'O') {
      closePeriodJob({ jobId: job.id?.toString() });
    }
    searchParams.delete(`confirm_close_job_${job.id}`);
    searchParams.delete(`confirm_hide_job_${job.id}`);
    setSearchParams(searchParams);
  }, [
    closePeriodJob,
    hidePeriodJob,
    job.id,
    job.status,
    searchParams,
    setSearchParams,
  ]);

  const ConfirmationMessage = useMemo(() => {
    return classNames(
      job.status === 'O' && localize('confirmCloseJob'),
      job.status === 'C' && localize('confirmHideJob'),
    );
  }, [job.status]);

  const bannerVariant:
    | 'warning'
    | 'success'
    | 'neutral'
    | 'neutral-var'
    | 'warning-var'
    | 'destructive' = useMemo(() => {
    if (job.status === 'O') return 'neutral';
    if (job.is_confirmed) return 'success';
    if (!job.is_confirmed && job.status === 'C') return 'destructive';
    return 'neutral-var';
  }, [job.is_confirmed, job.status]);

  if (!job) return <></>;
  return (
    <div className={cardClassName} style={{ boxShadow }}>
      <div className="flex flex-col items-start justify-start border-md pl-6">
        <Typography
          variant="body-large-bold"
          as="h5"
          className="pb-3 text-gray-800 flex items-center justify-between w-full"
        >
          {job.organisation}
          <Banner
            title={classNames(
              job.status === 'O' && localize('open'),
              job.is_confirmed && localize('confirmed'),
              !job.is_confirmed && job.status === 'C' && localize('closed'),
            )}
            variant={bannerVariant}
          />
        </Typography>
        <Typography
          variant="body-medium"
          as="h5"
          className="pb-4 text-gray-600"
        >
          {job.location}
        </Typography>
      </div>

      <div className="pl-6 md:pl-3 lg:pl-6 grid grid-cols-1 gap-x-2 sm:grid-cols-2 lg:grid-cols-3">
        <PairText
          title={localize('period')}
          icon={TbCalendar}
          value={`${formatDateToLocaleNetherlands(job.from_date)} tot 
          ${formatDateToLocaleNetherlands(job.to_date)}`}
        />

        <PairText
          title={localize('days')}
          className="mt-2"
          icon={TbRepeat}
          value={`${job.weekdays?.map((day, index) => {
            if (index === 0) return day;
            return ` ${day}`;
          })}`}
        />

        <PairText
          title={localize('time')}
          className="mt-2"
          icon={TbClock}
          value={`${formatJobDurationDisplay(
            job.start_time,
            job.end_time,
            job.pause.toString(),
          )}`}
        />

        <PairText
          title={localize('competences')}
          className="mt-2"
          icon={TbPuzzle}
          value={job.competence || ''}
        />

        <PairText
          title={localize('certificates')}
          className="mt-2"
          icon={TbPuzzle}
          value={classNames(
            job.certificates?.length && job.certificates?.join(', '),
            !job.certificates?.length && localize('noCertificatesRequired'),
          )}
        />

        <PairText
          title={localize('maximalTarif')}
          className="mt-2"
          icon={TbCurrencyEuro}
          value={`${`€${job.max_rate}  (excl) / €${
            !!job.max_rate && (job.max_rate * 1.21).toFixed(2)
          }  (incl) / €${job.max_rate_exempt} (vrijgesteld)`}`}
        />

        <div className="w-full flex-col md:flex-row flex col-span-1 sm:col-span-2 lg:col-span-3 items-center justify-end mt-6">
          <Button
            label={classNames(
              job.status === 'O' && localize('close'),
              job.status === 'C' && localize('hide'),
            )}
            variant="secondary"
            className="mb-3 md:mb-auto md:mr-2 w-full md:w-auto"
            onClick={handleHidingClosingActions}
          />
          <Button
            label={localize('edit')}
            variant="primary"
            className="mb-3 md:mb-auto md:mr-2 w-full md:w-auto"
            onClick={() => {
              window.location.assign(
                `/organisation/period_jobs/${job.id}/edit/`,
              );
            }}
          />

          <Button
            label={classNames(
              (job.status === 'O' || job.status === 'C') &&
                !job.is_confirmed &&
                localize('detailsAndConfirm'),
              job.is_confirmed && localize('assignmentDetails'),
            )}
            variant={job.is_confirmed ? 'secondary' : 'primary'}
            className={classNames('mb-3 md:mb-auto md:mr-2 w-full md:w-auto')}
            onClick={handleDetailsClicked}
          />
        </div>
      </div>
      <Modal
        isOpen={
          searchParams.get(`confirm_hide_job_${job.id}`) === 'true' ||
          searchParams.get(`confirm_close_job_${job.id}`) === 'true'
        }
        onClose={() => {
          searchParams.delete(`confirm_hide_job_${job.id}`);
          setSearchParams(searchParams);
        }}
        title={localize('confirmAction')}
        footer={{
          helpText: localize('clickToProceed', {
            action: classNames(
              job.status === 'O' && localize('close'),
              job.status === 'C' && localize('hide'),
            ),
          }),
          primaryButton: (
            <Button
              label={localize('confirm')}
              variant="danger"
              onClick={mutateJobStatus}
            />
          ),
          secondaryButton: (
            <Button
              label={localize('back')}
              variant="secondary"
              onClick={() => {
                searchParams.delete(`confirm_hide_job_${job.id}`);
                searchParams.delete(`confirm_close_job_${job.id}`);
                setSearchParams(searchParams);
              }}
            />
          ),
        }}
      >
        <Typography as="p" variant="body-large" className=" text-gray-700">
          {ConfirmationMessage}
        </Typography>
      </Modal>
    </div>
  );
};
export default PeriodJobCard;
